/**
 * SRE Scoring Engine
 * ==================
 * Deterministic matching engine based on Material DNA
 * ALL weights and thresholds from Blueprint config
 * 
 * This is NOT AI guessing - it's precise DNA comparison.
 */

// Default config (fallback if Blueprint not loaded)
const DEFAULT_WEIGHTS = {
  color_family: 30,
  pattern_type: 25,
  tone: 15,
  application_role: 15,
  visual_intensity: 10,
  contrast: 5
};

const DEFAULT_THRESHOLDS = {
  excellent: 80,
  strong: 65,
  medium: 50,
  weak: 30
};

const DEFAULT_LABELS = {
  excellent: 'Match perfetto',
  strong: 'Match forte',
  medium: 'Alternativa valida',
  weak: 'Similitudine parziale'
};

const DEFAULT_EXPLANATIONS = {
  color_family: 'Stessa famiglia cromatica',
  pattern_type: 'Pattern simile',
  tone: 'Tonalità compatibile',
  application_role: 'Stessa destinazione d\'uso',
  visual_intensity: 'Intensità visiva simile',
  contrast: 'Contrasto comparabile'
};

/**
 * Get SRE config from Blueprint or use defaults
 */
export const getSREConfig = (blueprintLogic = {}) => {
  const sre = blueprintLogic?.sre || {};
  
  return {
    weights: sre?.weights || DEFAULT_WEIGHTS,
    thresholds: sre?.thresholds || DEFAULT_THRESHOLDS,
    labels: sre?.labels || DEFAULT_LABELS,
    explanations: sre?.explanations || DEFAULT_EXPLANATIONS
  };
};

/**
 * Calculate similarity score between two materials
 * Returns score (0-100), label, and reasons
 */
export const calculateSREScore = (sourceMaterial, targetMaterial, config = {}) => {
  const { weights, thresholds, labels, explanations } = getSREConfig(config);
  
  if (!sourceMaterial || !targetMaterial) {
    return { score: 0, label: labels.weak, reasons: [] };
  }
  
  let totalScore = 0;
  const reasons = [];
  const breakdown = {};
  
  // Compare each DNA field
  Object.entries(weights).forEach(([field, weight]) => {
    const sourceValue = getFieldValue(sourceMaterial, field);
    const targetValue = getFieldValue(targetMaterial, field);
    
    if (sourceValue && targetValue) {
      const similarity = calculateFieldSimilarity(field, sourceValue, targetValue);
      const fieldScore = similarity * weight;
      totalScore += fieldScore;
      
      breakdown[field] = {
        source: sourceValue,
        target: targetValue,
        similarity,
        score: fieldScore,
        maxScore: weight
      };
      
      // Add to reasons if significant match
      if (similarity >= 0.7) {
        reasons.push({
          field,
          explanation: explanations[field] || `${field} compatibile`,
          strength: similarity >= 0.9 ? 'exact' : 'similar'
        });
      }
    }
  });
  
  // Determine label based on thresholds
  let label = labels.weak;
  if (totalScore >= thresholds.excellent) {
    label = labels.excellent;
  } else if (totalScore >= thresholds.strong) {
    label = labels.strong;
  } else if (totalScore >= thresholds.medium) {
    label = labels.medium;
  }
  
  return {
    score: Math.round(totalScore),
    label,
    reasons,
    breakdown
  };
};

/**
 * Get field value from material (handles nested DNA)
 */
const getFieldValue = (material, field) => {
  // Try direct field
  if (material[field] != null) return material[field];
  
  // Try nested DNA object
  const dna = material.material_dna?.[0] || material.material_dna || material.dna;
  if (dna?.[field] != null) return dna[field];
  
  // Try alternative field names
  const alternatives = {
    color_family: ['dominant_color', 'color'],
    pattern_type: ['pattern']
  };
  
  for (const alt of (alternatives[field] || [])) {
    if (material[alt] != null) return material[alt];
    if (dna?.[alt] != null) return dna[alt];
  }
  
  return null;
};

/**
 * Calculate similarity between two field values (0-1)
 */
const calculateFieldSimilarity = (field, value1, value2) => {
  if (value1 === value2) return 1.0;
  
  const v1 = String(value1).toLowerCase().trim();
  const v2 = String(value2).toLowerCase().trim();
  
  if (v1 === v2) return 1.0;
  
  // Field-specific similarity rules
  switch (field) {
    case 'color_family':
      return calculateColorSimilarity(v1, v2);
    
    case 'tone':
      return calculateToneSimilarity(v1, v2);
    
    case 'visual_intensity':
    case 'contrast':
      return calculateNumericSimilarity(value1, value2);
    
    case 'pattern_type':
      return calculatePatternSimilarity(v1, v2);
    
    case 'application_role':
      return calculateApplicationSimilarity(v1, v2);
    
    default:
      return v1 === v2 ? 1.0 : 0.0;
  }
};

/**
 * Color family similarity
 */
const COLOR_FAMILIES = {
  light: ['white', 'cream', 'ivory', 'beige', 'light'],
  dark: ['black', 'charcoal', 'ebony', 'nero', 'dark'],
  warm: ['gold', 'honey', 'amber', 'brown', 'terracotta', 'warm'],
  cool: ['grey', 'gray', 'silver', 'blue', 'green', 'cool'],
  neutral: ['taupe', 'greige', 'sand', 'neutral']
};

const calculateColorSimilarity = (c1, c2) => {
  // Find families for each color
  const family1 = Object.entries(COLOR_FAMILIES).find(([, colors]) => 
    colors.some(c => c1.includes(c) || c.includes(c1))
  )?.[0];
  
  const family2 = Object.entries(COLOR_FAMILIES).find(([, colors]) => 
    colors.some(c => c2.includes(c) || c.includes(c2))
  )?.[0];
  
  if (family1 && family1 === family2) return 0.8;
  
  // Light/Dark are opposites
  if ((family1 === 'light' && family2 === 'dark') || 
      (family1 === 'dark' && family2 === 'light')) {
    return 0.1;
  }
  
  // Warm/Cool are somewhat different
  if ((family1 === 'warm' && family2 === 'cool') || 
      (family1 === 'cool' && family2 === 'warm')) {
    return 0.3;
  }
  
  return 0.4; // Default partial similarity
};

/**
 * Tone similarity
 */
const calculateToneSimilarity = (t1, t2) => {
  const warmTones = ['warm', 'golden', 'honey', 'amber'];
  const coolTones = ['cool', 'silver', 'grey', 'blue'];
  const neutralTones = ['neutral', 'balanced'];
  
  const isWarm1 = warmTones.some(t => t1.includes(t));
  const isWarm2 = warmTones.some(t => t2.includes(t));
  const isCool1 = coolTones.some(t => t1.includes(t));
  const isCool2 = coolTones.some(t => t2.includes(t));
  
  if ((isWarm1 && isWarm2) || (isCool1 && isCool2)) return 0.9;
  if ((isWarm1 && isCool2) || (isCool1 && isWarm2)) return 0.2;
  
  return 0.5;
};

/**
 * Numeric similarity (for intensity, contrast)
 */
const calculateNumericSimilarity = (v1, v2) => {
  const n1 = parseFloat(v1) || 0;
  const n2 = parseFloat(v2) || 0;
  
  if (n1 === 0 && n2 === 0) return 0.5;
  
  const diff = Math.abs(n1 - n2);
  const max = Math.max(n1, n2, 1);
  
  return Math.max(0, 1 - (diff / max));
};

/**
 * Pattern similarity
 */
const PATTERN_GROUPS = {
  veined: ['veined', 'vein', 'venato', 'venatura'],
  uniform: ['uniform', 'solid', 'plain', 'homogeneous'],
  dramatic: ['dramatic', 'bold', 'striking', 'movement'],
  soft: ['soft', 'subtle', 'delicate', 'fine'],
  crystalline: ['crystalline', 'crystal', 'sparkle', 'granular'],
  brecciated: ['brecciated', 'breccia', 'fragment', 'mosaic']
};

const calculatePatternSimilarity = (p1, p2) => {
  const group1 = Object.entries(PATTERN_GROUPS).find(([, patterns]) =>
    patterns.some(p => p1.includes(p))
  )?.[0];
  
  const group2 = Object.entries(PATTERN_GROUPS).find(([, patterns]) =>
    patterns.some(p => p2.includes(p))
  )?.[0];
  
  if (group1 && group1 === group2) return 0.9;
  
  // Similar groups
  if ((group1 === 'veined' && group2 === 'dramatic') ||
      (group1 === 'dramatic' && group2 === 'veined')) return 0.6;
  
  if ((group1 === 'uniform' && group2 === 'soft') ||
      (group1 === 'soft' && group2 === 'uniform')) return 0.7;
  
  return 0.3;
};

/**
 * Application role similarity
 */
const calculateApplicationSimilarity = (a1, a2) => {
  // Parse comma-separated roles
  const roles1 = a1.split(/[,;]/).map(r => r.trim().toLowerCase());
  const roles2 = a2.split(/[,;]/).map(r => r.trim().toLowerCase());
  
  // Count matching roles
  const matches = roles1.filter(r1 => 
    roles2.some(r2 => r1.includes(r2) || r2.includes(r1))
  ).length;
  
  if (matches > 0) {
    return Math.min(1, matches / Math.max(roles1.length, roles2.length) + 0.3);
  }
  
  return 0.2;
};

/**
 * Get related materials with SRE scores
 */
export const getRelatedWithSRE = (sourceMaterial, allMaterials, config = {}, limit = 4) => {
  if (!sourceMaterial || !allMaterials?.length) return [];
  
  const scored = allMaterials
    .filter(m => m.id !== sourceMaterial.id)
    .map(material => ({
      material,
      ...calculateSREScore(sourceMaterial, material, config)
    }))
    .filter(item => item.score >= (config?.logic?.sre?.thresholds?.weak || 30))
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
  
  return scored;
};

// =============================================================================
// SIMILARITY NAVIGATION SYSTEM
// =============================================================================
// 3 Groups based on DESIGN INTENT, not just score ranges:
// - Similar Materials: same visual language
// - Strong Alternatives: same function, different expression  
// - Contrast Alternatives: intentional design tension
// =============================================================================

/**
 * Similarity group definitions (design intent)
 */
const SIMILARITY_GROUPS = {
  similar: {
    key: 'similar',
    minScore: 65,
    maxScore: 100,
    // Must share: color family OR pattern type
    requireMatch: ['color_family', 'pattern_type'],
    matchMode: 'any' // at least one must match strongly
  },
  strongAlternatives: {
    key: 'strong_alternatives',
    minScore: 45,
    maxScore: 70,
    // Same function (application), different visual expression
    requireMatch: ['application_role'],
    requireDifference: ['color_family', 'pattern_type'],
    matchMode: 'function' // same use, different look
  },
  contrastAlternatives: {
    key: 'contrast_alternatives',
    minScore: 20,
    maxScore: 50,
    // Intentional design tension - opposites
    preferOpposites: ['color_family', 'tone'],
    matchMode: 'contrast' // deliberate contrast
  }
};

/**
 * Check if materials share strong similarity on specific fields
 */
const hasStrongMatch = (source, target, fields) => {
  for (const field of fields) {
    const sourceVal = getFieldValue(source, field);
    const targetVal = getFieldValue(target, field);
    
    if (sourceVal && targetVal) {
      const similarity = calculateFieldSimilarity(field, sourceVal, targetVal);
      if (similarity >= 0.7) return true;
    }
  }
  return false;
};

/**
 * Check if materials have meaningful differences on specific fields
 */
const hasDifference = (source, target, fields) => {
  for (const field of fields) {
    const sourceVal = getFieldValue(source, field);
    const targetVal = getFieldValue(target, field);
    
    if (sourceVal && targetVal) {
      const similarity = calculateFieldSimilarity(field, sourceVal, targetVal);
      if (similarity < 0.5) return true;
    }
  }
  return false;
};

/**
 * Check if materials are contrasting (opposites)
 */
const hasContrast = (source, target, fields) => {
  for (const field of fields) {
    const sourceVal = getFieldValue(source, field);
    const targetVal = getFieldValue(target, field);
    
    if (sourceVal && targetVal) {
      const similarity = calculateFieldSimilarity(field, sourceVal, targetVal);
      if (similarity <= 0.3) return true; // Strong contrast
    }
  }
  return false;
};

/**
 * Categorize a scored material into a similarity group
 */
const categorizeMaterial = (scoredItem, sourceMaterial) => {
  const { score, material } = scoredItem;
  
  // Check for Similar Materials (same visual language)
  const similarGroup = SIMILARITY_GROUPS.similar;
  if (score >= similarGroup.minScore && score <= similarGroup.maxScore) {
    if (hasStrongMatch(sourceMaterial, material, similarGroup.requireMatch)) {
      return 'similar';
    }
  }
  
  // Check for Strong Alternatives (same function, different expression)
  const altGroup = SIMILARITY_GROUPS.strongAlternatives;
  if (score >= altGroup.minScore && score <= altGroup.maxScore) {
    if (hasStrongMatch(sourceMaterial, material, altGroup.requireMatch) &&
        hasDifference(sourceMaterial, material, altGroup.requireDifference)) {
      return 'strong_alternatives';
    }
  }
  
  // Check for Contrast Alternatives (intentional tension)
  const contrastGroup = SIMILARITY_GROUPS.contrastAlternatives;
  if (score >= contrastGroup.minScore && score <= contrastGroup.maxScore) {
    if (hasContrast(sourceMaterial, material, contrastGroup.preferOpposites)) {
      return 'contrast_alternatives';
    }
  }
  
  // Default categorization based on score
  if (score >= 65) return 'similar';
  if (score >= 40) return 'strong_alternatives';
  if (score >= 20) return 'contrast_alternatives';
  
  return null;
};

/**
 * Get grouped similarity navigation
 * Returns 3 groups: similar, strongAlternatives, contrastAlternatives
 */
export const getSimilarityNavigation = (sourceMaterial, allMaterials, config = {}, limits = { similar: 4, strongAlternatives: 4, contrastAlternatives: 4 }) => {
  if (!sourceMaterial || !allMaterials?.length) {
    return { similar: [], strongAlternatives: [], contrastAlternatives: [] };
  }
  
  const sreConfig = getSREConfig(config?.logic || config);
  
  // Score all materials
  const scoredMaterials = allMaterials
    .filter(m => m.id !== sourceMaterial.id)
    .map(material => ({
      material,
      ...calculateSREScore(sourceMaterial, material, config?.logic || config)
    }))
    .filter(item => item.score >= 15); // Lower threshold to catch contrasts
  
  // Categorize into groups
  const groups = {
    similar: [],
    strongAlternatives: [],
    contrastAlternatives: []
  };
  
  // First pass - strict categorization
  scoredMaterials.forEach(item => {
    const category = categorizeMaterial(item, sourceMaterial);
    if (category && groups[category]) {
      groups[category].push(item);
    }
  });
  
  // Sort each group by score and apply limits
  Object.keys(groups).forEach(key => {
    groups[key] = groups[key]
      .sort((a, b) => b.score - a.score)
      .slice(0, limits[key] || 4);
  });
  
  // If a group is empty, try to fill it with fallback logic
  if (groups.similar.length === 0) {
    groups.similar = scoredMaterials
      .filter(item => item.score >= 50)
      .sort((a, b) => b.score - a.score)
      .slice(0, limits.similar || 4);
  }
  
  if (groups.strongAlternatives.length === 0) {
    groups.strongAlternatives = scoredMaterials
      .filter(item => item.score >= 35 && item.score < 65)
      .filter(item => !groups.similar.includes(item))
      .sort((a, b) => b.score - a.score)
      .slice(0, limits.strongAlternatives || 4);
  }
  
  if (groups.contrastAlternatives.length === 0) {
    groups.contrastAlternatives = scoredMaterials
      .filter(item => item.score >= 15 && item.score < 45)
      .filter(item => !groups.similar.includes(item) && !groups.strongAlternatives.includes(item))
      .sort((a, b) => b.score - a.score)
      .slice(0, limits.contrastAlternatives || 4);
  }
  
  return groups;
};

/**
 * Get labels for similarity groups from config
 */
export const getSimilarityGroupLabels = (config = {}) => {
  const labels = config?.content?.material?.similarity || {};
  
  return {
    similar: {
      title: labels.similar_title || 'Materiali Simili',
      description: labels.similar_description || 'Stesso linguaggio visivo',
      intent: 'same_visual'
    },
    strongAlternatives: {
      title: labels.alternatives_title || 'Alternative Valide',
      description: labels.alternatives_description || 'Stessa funzione, espressione diversa',
      intent: 'same_function'
    },
    contrastAlternatives: {
      title: labels.contrast_title || 'Alternative a Contrasto',
      description: labels.contrast_description || 'Tensione progettuale intenzionale',
      intent: 'design_tension'
    }
  };
};

/**
 * Format score for display
 */
export const formatScore = (score) => {
  return `${score}%`;
};

/**
 * Get score color class based on thresholds
 */
export const getScoreColor = (score, thresholds = DEFAULT_THRESHOLDS) => {
  if (score >= thresholds.excellent) return 'text-emerald-600';
  if (score >= thresholds.strong) return 'text-[var(--gold)]';
  if (score >= thresholds.medium) return 'text-amber-600';
  return 'text-gray-500';
};

export default {
  getSREConfig,
  calculateSREScore,
  getRelatedWithSRE,
  getSimilarityNavigation,
  getSimilarityGroupLabels,
  formatScore,
  getScoreColor
};
